import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { magicQuoteApi, type MagicQuoteUploadFields } from '../api/endpoints.ts';
import { isLocalWalkthroughId, resolveWalkthroughId } from './walkthrough-id.ts';

/**
 * The offline capture queue.
 *
 * A walkthrough happens inside somebody's house, which is the worst place a
 * phone ever has to work: one bar in the basement, nothing at all in the
 * master bath, and a customer standing right there watching the cleaner look
 * at their screen. So capture is designed around the assumption that the
 * network is not available, rather than around handling the case where it
 * isn't:
 *
 *  1. Every capture is written to IndexedDB **first**, blob and all, and only
 *     then offered to the server. Pressing the shutter cannot fail.
 *  2. Each carries a `clientCaptureId` minted at capture time. The server
 *     treats a repeat of that id within a session as the same capture, so the
 *     queue can retry as often as it likes — including the case it cannot
 *     detect, where the upload succeeded and the response never arrived.
 *  3. Nothing is deleted from the local store until the server has
 *     acknowledged it. Closing the app, killing the tab, or walking out of
 *     range mid-upload costs a retry, never a photo.
 *
 * Retries back off, and a capture that keeps failing stays in the queue and
 * says so rather than being dropped — the cleaner can be told "3 waiting to
 * upload" and drive to somewhere with signal, which is a real remedy. Only a
 * capture the *server* refuses on its merits (a 4xx that is not a timeout or
 * an auth blip) is given up on, because retrying that one forever would keep
 * a permanently stuck item in front of them.
 */

const DB_NAME = 'magic-quote-captures';
const DB_VERSION = 1;
const STORE = 'pending';

export type QueuedKind = 'media' | 'note';

export interface QueuedCapture {
  /** The client-minted id. Also the primary key, and the server's dedupe key. */
  clientCaptureId: string;
  sessionId: string;
  kind: QueuedKind;
  /** Media only. */
  blob?: Blob;
  fields?: MagicQuoteUploadFields;
  /** Notes and room markers only. */
  note?: { type: 'text' | 'room_marker'; text: string };
  capturedAt: string;
  attempts: number;
  /** Epoch ms before which this should not be retried. */
  nextAttemptAt: number;
  lastError: string | null;
  /** Set when the server refused it on its merits; no longer retried. */
  abandoned: boolean;
}

interface QueueSchema extends DBSchema {
  [STORE]: {
    key: string;
    value: QueuedCapture;
    indexes: { 'by-session': string };
  };
}

let dbPromise: Promise<IDBPDatabase<QueueSchema>> | null = null;

function db(): Promise<IDBPDatabase<QueueSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<QueueSchema>(DB_NAME, DB_VERSION, {
      upgrade(database) {
        const store = database.createObjectStore(STORE, { keyPath: 'clientCaptureId' });
        store.createIndex('by-session', 'sessionId');
      },
      // A connection the browser closed under us — storage reclaimed, the tab
      // put to sleep and woken. Dropping the handle means the next call opens
      // a new one instead of writing into a dead connection.
      terminated() {
        dbPromise = null;
      },
    });
    // A failed open must not be remembered as the answer. `openDB` rejects for
    // reasons that pass — a private window, storage pressure, another tab
    // holding an older version open during an upgrade — and caching the
    // rejected promise made one bad moment permanent: every later enqueue,
    // flush and read for the life of the page took the `safely` fallback, so
    // captures stopped draining and the only cure anyone could find was
    // reloading. Forgetting it means the next attempt is a real attempt.
    dbPromise.catch(() => {
      dbPromise = null;
    });
  }
  return dbPromise;
}

/**
 * IndexedDB is unavailable in private modes and some embedded browsers.
 *
 * Unlike the quote draft store — where local storage is an enhancement — this
 * failing means the queue cannot hold anything, so `enqueue` reports it and
 * the caller uploads directly instead. Degraded (no offline safety net) beats
 * broken (no capture at all).
 */
async function safely<T>(operation: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await operation();
  } catch {
    return fallback;
  }
}

export function newClientCaptureId(): string {
  // Not a ULID: this only has to be unique within one session and comparable
  // as a key. crypto.randomUUID is available everywhere a camera is.
  return crypto.randomUUID();
}

/** Backoff: 1s, 2s, 4s, … capped at a minute. */
function backoffMs(attempts: number): number {
  return Math.min(60_000, 1000 * 2 ** Math.min(attempts, 6));
}

export async function enqueueMedia(sessionId: string, blob: Blob, fields: MagicQuoteUploadFields): Promise<boolean> {
  return safely(async () => {
    const database = await db();
    await database.put(STORE, {
      clientCaptureId: fields.clientCaptureId,
      // Resolved at write time, not at capture time. A walkthrough handed over
      // while its screen is open keeps saying `local-…` until the screen
      // notices, and a photo written under that id afterwards would belong to
      // nothing — see `resolveWalkthroughId`.
      sessionId: resolveWalkthroughId(sessionId),
      kind: 'media',
      blob,
      fields,
      capturedAt: fields.capturedAt,
      attempts: 0,
      nextAttemptAt: 0,
      lastError: null,
      abandoned: false,
    });
    return true;
  }, false);
}

export async function enqueueNote(
  sessionId: string,
  note: { type: 'text' | 'room_marker'; text: string },
  clientCaptureId: string,
  capturedAt: string,
): Promise<boolean> {
  return safely(async () => {
    const database = await db();
    await database.put(STORE, {
      clientCaptureId,
      sessionId: resolveWalkthroughId(sessionId),
      kind: 'note',
      note,
      capturedAt,
      attempts: 0,
      nextAttemptAt: 0,
      lastError: null,
      abandoned: false,
    });
    return true;
  }, false);
}

/**
 * Everything queued for a session, in the order it was captured.
 *
 * The sort is load-bearing, not tidiness. Every row for a session shares one
 * index key, so IndexedDB breaks the tie by primary key — which here is a
 * random UUID. Uploading in that order would hand the server a shuffled
 * walkthrough, and the server assigns `sequence_number` from arrival order:
 * room markers would land after the photos they were meant to file, and
 * synthesis reads that sequence to decide which room a photo belongs to. An
 * offline walkthrough that drains all at once is exactly when this bites.
 */
export async function pendingFor(sessionId: string): Promise<QueuedCapture[]> {
  return safely(async () => {
    const database = await db();
    const items = await database.getAllFromIndex(STORE, 'by-session', resolveWalkthroughId(sessionId));
    return items.sort((a, b) => a.capturedAt.localeCompare(b.capturedAt));
  }, []);
}

export interface FlushResult {
  uploaded: number;
  /** Still queued: waiting on the network, or on their backoff. */
  pending: number;
  /** Refused on their merits and no longer retried. */
  abandoned: number;
  /**
   * Why the queue is not moving, when it is not moving.
   *
   * A count of waiting captures is not a diagnosis. "3 waiting to upload" with
   * no reason is indistinguishable from a bug, and it is what an estimator is
   * looking at while deciding whether the software is broken — so the message
   * from the item at the head of the queue comes back with the count.
   */
  lastError: string | null;
}

/**
 * Tries every queued capture for a session that is due.
 *
 * Serial rather than parallel on purpose: a phone on one bar uploading six
 * photos at once finishes none of them.
 *
 * And strictly in order, which is the stronger promise. The server numbers
 * captures by arrival, not by `capturedAt`, and that sequence is what
 * synthesis reads as the walk: a room marker is what makes the next six
 * photos "the master bathroom". So an item that is not yet due, or that has
 * just failed, stops the drain rather than letting its successors overtake
 * it — a marker arriving after the photos it was meant to classify puts them
 * in the wrong room, and prices them there.
 *
 * The cost is bounded: backoff caps at a minute, so the worst a stalled item
 * does is hold the queue for that long, and the flush is retried on a timer.
 * An abandoned item is the exception — it is never going to arrive, so
 * waiting on it would stall the queue forever rather than for a minute.
 */
/**
 * The flush in progress for a session, so a second one waits rather than races.
 *
 * Concurrency here is not hypothetical, it is the normal case. Every queued
 * capture kicks a flush, and the things that queue captures arrive in bursts:
 * stopping a video queues the clip, up to three sampled frames and the split
 * audio track back to back, and picking six photos from the library queues six.
 * That is five or six flushes starting inside a second, each reading its own
 * snapshot of the same queue and uploading the same leading items in parallel.
 *
 * The server's insert is idempotent, so no duplicate capture rows appear — but
 * everything before the insert has already happened several times over: several
 * multi-megabyte bodies pushed through one bar of signal, several R2 objects
 * written, and several bites out of the company's upload allowance. Worse, a
 * flush that fails an item writes its backoff record with `put`, which
 * re-inserts a row a concurrent flush had already uploaded and deleted: the
 * queue never empties, Finish stays disabled, and the only thing that helps is
 * reloading the page. Which is exactly what was reported.
 *
 * Chained rather than coalesced: every caller's flush really runs, just one
 * after another, so a capture queued while a flush is mid-flight is never
 * missed by the pass that was already in the air.
 */
const inFlight = new Map<string, Promise<unknown>>();

export function flush(id: string, options: { force?: boolean } = {}): Promise<FlushResult> {
  // The screen that started an offline walkthrough goes on asking for its
  // local id for as long as it is open; after the handover, that id names the
  // server's walkthrough, and this is what drains it.
  const sessionId = resolveWalkthroughId(id);
  const previous = inFlight.get(sessionId) ?? Promise.resolve();
  const next = previous.then(
    () => flushOnce(sessionId, options),
    () => flushOnce(sessionId, options),
  );
  // Held as a settled-either-way promise so one failure cannot poison the
  // chain, and cleared when this is the last link so the map does not grow.
  const guard = next.catch(() => undefined);
  inFlight.set(sessionId, guard);
  void guard.then(() => {
    if (inFlight.get(sessionId) === guard) inFlight.delete(sessionId);
  });
  return next;
}

async function flushOnce(sessionId: string, options: { force?: boolean }): Promise<FlushResult> {
  if (cleared.has(sessionId)) return { uploaded: 0, pending: 0, abandoned: 0, lastError: null };
  const queued = await pendingFor(sessionId);

  /**
   * A walkthrough the server has not been told about yet holds its captures
   * and sends nothing.
   *
   * This is a guard, not an optimization, and getting it wrong would be
   * expensive: uploading against a device-minted id asks the server for a
   * walkthrough that does not exist, which is a 404 — a 4xx, which
   * `isPermanent` reads (correctly, for every id the server ever issued) as
   * "refused on its merits". Every photo of the customer's house would be
   * marked abandoned within one flush, and the walk would be over before the
   * cleaner reached the second bedroom.
   *
   * The captures wait here until the walkthrough is handed over and re-keyed
   * (see `reassignSession`), at which point the ordinary drain does the rest.
   */
  if (isLocalWalkthroughId(sessionId)) {
    return {
      uploaded: 0,
      pending: queued.filter((item) => !item.abandoned).length,
      abandoned: queued.filter((item) => item.abandoned).length,
      lastError: null,
    };
  }

  const now = Date.now();
  let uploaded = 0;
  let abandoned = 0;

  for (const item of queued) {
    if (item.abandoned) {
      abandoned += 1;
      continue;
    }
    // `force` is a person pressing Retry, and it has to mean something. Without
    // it the button was a no-op through the whole backoff window — up to a
    // minute of a screen that says captures are waiting and a button that does
    // nothing when pressed, which is precisely when somebody gives up and
    // reloads the page. Asking again immediately is what they asked for.
    if (!options.force && item.nextAttemptAt > now) break;

    try {
      if (item.kind === 'media' && item.blob && item.fields) {
        await magicQuoteApi.uploadCapture(sessionId, item.blob, item.fields);
      } else if (item.kind === 'note' && item.note) {
        await magicQuoteApi.addNote(sessionId, {
          ...item.note,
          clientCaptureId: item.clientCaptureId,
          capturedAt: item.capturedAt,
        });
      } else {
        // A row with neither payload cannot ever be uploaded; dropping it is
        // the only thing that stops it being retried forever.
        await remove(item.clientCaptureId);
        continue;
      }
      await remove(item.clientCaptureId);
      uploaded += 1;
    } catch (error) {
      const permanent = isPermanent(error);
      if (permanent) abandoned += 1;
      // Nothing is written back to a session somebody has deleted. This is the
      // failure path of an upload that was in flight when the walkthrough went
      // away, and its `put` would restore the very row the deletion removed.
      if (cleared.has(sessionId)) break;
      await safely(async () => {
        const database = await db();
        await database.put(STORE, {
          ...item,
          attempts: item.attempts + 1,
          nextAttemptAt: Date.now() + backoffMs(item.attempts + 1),
          lastError: error instanceof Error ? error.message : 'Upload failed',
          abandoned: permanent,
        });
      }, undefined);
      // A failure that will be retried holds its place in the walk; one that
      // has been abandoned has given it up, and the rest of the queue should
      // not wait for something that is never coming.
      if (!permanent) break;
    }
  }

  const remaining = await pendingFor(sessionId);
  const stuck = remaining.find((item) => item.lastError);
  return {
    uploaded,
    pending: remaining.filter((item) => !item.abandoned).length,
    abandoned: remaining.filter((item) => item.abandoned).length,
    lastError: stuck?.lastError ?? null,
  };
}

/**
 * Whether the server has decided about this capture rather than failed to
 * hear it.
 *
 * A 4xx means the request itself is unacceptable — wrong type, too large, the
 * walkthrough already generated its quote — and repeating it will produce the
 * same refusal. 401 and 403 are excluded because they are usually a session
 * that needs refreshing rather than a capture that is wrong, and 408/429 are
 * explicitly "ask again". Everything else, including a total absence of a
 * response, is a network problem and stays in the queue.
 *
 * The one 403 that IS a decision says so in its code: a company that switched
 * video or audio off will refuse that clip on every flush for the rest of
 * time, and a queue that keeps retrying it never empties — which is the thing
 * standing between the cleaner and finishing the walkthrough.
 *
 * The mirror image is the one 409 that is NOT a decision. A capture that lands
 * while Organize holds the walkthrough is refused because the walkthrough is
 * busy for a couple of minutes, not because there is anything wrong with the
 * capture — and Organize is exactly when a cleaner is still walking and still
 * capturing. Abandoning it there loses the photo outright: abandoned items are
 * skipped by every later flush and are not counted in `pending`, so the screen
 * says nothing is waiting and the walkthrough can be finished without it. The
 * other two conflicts on that path — the walkthrough was deleted, it already
 * has a quote — are final and must stay permanent, which is why this turns on
 * the CODE and not on the status.
 */
function isPermanent(error: unknown): boolean {
  const { status, code } = (error ?? {}) as { status?: number; code?: string };
  if (typeof status !== 'number') return false;
  if (status === 403 && code === 'CAPTURE_TYPE_DISABLED') return true;
  if (status === 409 && code === 'WALKTHROUGH_ORGANIZING') return false;
  if (status === 401 || status === 403 || status === 408 || status === 429) return false;
  return status >= 400 && status < 500;
}

/**
 * Moves a walkthrough's queued captures onto the id the server gave it.
 *
 * The last step of handing an offline walkthrough over. Everything captured on
 * the doorstep and in the basement is filed under the id the device minted,
 * and none of it can be uploaded under that name — so when the server answers
 * with a real id, the queue is re-keyed and the ordinary drain takes over.
 *
 * `capturedAt` is what preserves the walk through the move, not the order rows
 * happen to come back in: `pendingFor` sorts by it, and that sort is what
 * keeps a room marker in front of the six photos it names.
 *
 * Idempotent, because the handover it belongs to is: a second call finds
 * nothing left under the old id and does nothing.
 *
 * Returns how many moved, or NULL if the store refused — and the difference
 * matters more than it looks. Both used to come back as zero, which the
 * handover read as "nothing to move" and went on to mark the walkthrough
 * adopted: the record left the pending list while its captures stayed under an
 * id nothing would ever drain again, and the walkthrough finished on the
 * server without them. A failure has to be able to stop the handover.
 */
export async function reassignSession(fromSessionId: string, toSessionId: string): Promise<number | null> {
  return safely(async () => {
    const database = await db();
    const items = await database.getAllFromIndex(STORE, 'by-session', fromSessionId);
    // One transaction: a re-key interrupted halfway — the tab closed, the
    // phone slept — would otherwise leave one walk split across two ids, and
    // the half still under the local id would never upload.
    const tx = database.transaction(STORE, 'readwrite');
    await Promise.all([...items.map((item) => tx.store.put({ ...item, sessionId: toSessionId })), tx.done]);
    return items.length;
    // Null, not zero, and the whole point of the return type: a store that
    // refused must not be indistinguishable from a walkthrough that had
    // nothing queued. See the caller in `local-walkthrough.ts`, which stops
    // the handover on a null rather than marking the walkthrough adopted over
    // captures it could not move.
  }, null);
}

export async function remove(clientCaptureId: string): Promise<void> {
  await safely(async () => {
    const database = await db();
    await database.delete(STORE, clientCaptureId);
  }, undefined);
}

/**
 * Sessions whose queue has been thrown away.
 *
 * A tombstone rather than just a delete, because deleting is not enough on its
 * own: a flush already uploading when the walkthrough is deleted receives its
 * 409 AFTER the rows are gone, and its failure path writes the item back with
 * `put` — recreating the customer's photo as an abandoned queue entry moments
 * after the confirmation said it was removed. The tombstone is what lets that
 * writeback know it is writing to a grave.
 *
 * Held for the life of the page, which is all it needs to outlive: the rows
 * are gone from IndexedDB, so a later reload has nothing to resurrect.
 */
const cleared = new Set<string>();

/** Clears a session's queue — used when a walkthrough is abandoned outright. */
export async function clearSession(sessionId: string): Promise<void> {
  cleared.add(sessionId);
  await safely(async () => {
    const database = await db();
    const items = await database.getAllFromIndex(STORE, 'by-session', sessionId);
    await Promise.all(items.map((item) => database.delete(STORE, item.clientCaptureId)));
  }, undefined);
}
